import { File } from "@/types/breeze/File";
import { CheckCircle, Globe, Lock } from "lucide-react";
import { FileActions } from "./FileActions";
import { Box, Flex, Text, Badge, IconButton, Card } from "@radix-ui/themes";
import toast from "react-hot-toast";
import FileDisplay from "./FileDisplay";
import { formatFileSize, formatDimensions, formatDuration } from "@/utils/fileUtils";

interface FileCardProps {
  file: File;
  onClick: () => void;
  onDelete: () => void;
  onEdit?: () => void;
  isSelected: boolean;
  onToggleSelect: () => void;
  isDisabled?: boolean;
  compact?: boolean;
  showActions?: boolean;
}

export function FileCard({
  file,
  onClick,
  onDelete,
  onEdit,
  isSelected,
  onToggleSelect,
  isDisabled = false,
  compact = false,
  showActions = true,
}: FileCardProps) {
  const formattedSize = formatFileSize(file.file_size);
  const contentHeight = compact ? "96px" : "128px";
  const iconSize = compact ? 14 : 16;
  const badgeSize = compact ? "1" : "2";

  const handleCopyPublicLink = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (file.is_public && file.cdn_url) {
      await navigator.clipboard.writeText(file.cdn_url);
      toast.success("Public link copied! 🔗", {
        duration: 3000,
      });
    }
  };

  return (
    <Card
      size="1"
      style={{
        position: "relative",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        opacity: isDisabled ? 0.6 : 1,
        cursor: isDisabled ? "not-allowed" : "pointer",
      }}
    >
      {/* Selection overlay - covers entire card */}
      <Box
        style={{
          position: "absolute",
          inset: 0,
          backgroundColor: isSelected
            ? "rgba(59, 130, 246, 0.1)"
            : "transparent",
          transition: "background-color 0.2s ease",
          zIndex: 5,
          pointerEvents: "none",
        }}
      />

      {/* Selection button */}
      <Box
        style={{ position: "absolute", left: "8px", top: "8px", zIndex: 10 }}
      >
        <IconButton
          variant={isSelected ? "solid" : "ghost"}
          color={isSelected ? "blue" : "gray"}
          size="1"
          disabled={isDisabled && !isSelected}
          style={{
            borderRadius: "9999px",
            backgroundColor: isSelected
              ? "var(--blue-9)"
              : isDisabled
                ? "rgba(107, 114, 128, 0.5)"
                : "rgba(0, 0, 0, 0.5)",
            color: "white",
            border: isSelected ? "none" : "1px solid rgba(255, 255, 255, 0.7)",
            cursor: isDisabled && !isSelected ? "not-allowed" : "pointer",
          }}
          onClick={(e) => {
            e.stopPropagation();
            if (!isDisabled || isSelected) {
              onToggleSelect();
            }
          }}
        >
          <CheckCircle size={iconSize} />
        </IconButton>
      </Box>

      {/* Public/Private indicator */}
      <Box
        style={{ position: "absolute", right: "8px", top: "8px", zIndex: 10 }}
      >
        <Badge
          variant="solid"
          color={file.is_public ? "green" : "gray"}
          size={badgeSize}
          style={{
            borderRadius: "9999px",
            backgroundColor: file.is_public
              ? "rgba(34, 197, 94, 0.9)"
              : "rgba(107, 114, 128, 0.9)",
            color: "white",
            cursor: file.is_public ? "pointer" : "default",
            transition: "transform 0.1s ease",
          }}
          title={
            file.is_public ? "Public file - Click to copy link" : "Private file"
          }
          onClick={file.is_public ? handleCopyPublicLink : undefined}
          onMouseDown={(e: React.MouseEvent) => {
            if (file.is_public) {
              (e.target as HTMLElement).style.transform = "scale(0.95)";
            }
          }}
          onMouseUp={(e: React.MouseEvent) => {
            if (file.is_public) {
              (e.target as HTMLElement).style.transform = "scale(1)";
            }
          }}
          onMouseLeave={(e: React.MouseEvent) => {
            if (file.is_public) {
              (e.target as HTMLElement).style.transform = "scale(1)";
            }
          }}
        >
          <Flex align="center" gap="1">
            {file.is_public ? <Globe size={12} /> : <Lock size={12} />}
            <Text size="1" weight="medium">
              {file.is_public ? "Public" : "Private"}
            </Text>
          </Flex>
        </Badge>
      </Box>

      {/* File Content */}
      <Box style={{ flex: "1" }}>
        <Box
          style={{
            height: contentHeight,
            cursor: "pointer",
            position: "relative",
          }}
          onClick={() => {
            // For images, open gallery; for non-images, trigger onClick (usually download)
            onClick?.();
          }}
        >
          <FileDisplay
            files={[file]}
            enableGallery={false} // We handle clicks at the FileCard level
            insideCarousel={true}
          />
        </Box>
      </Box>

      {/* File Info */}
      <Box p="2">
        <Text
          as="div"
          size="2"
          weight="medium"
          style={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
          title={file.original_file_name}
        >
          {file.original_file_name}
        </Text>
        <Text size="1" color="gray">
          {[
            formattedSize,
            formatDimensions(file.width, file.height),
            formatDuration(file.duration_seconds),
          ].filter(Boolean).join(" · ")}
        </Text>
        {showActions && (
          <Box mt="2">
            <FileActions file={file} onDelete={onDelete} onEdit={onEdit} minimal={compact} showDelete={false} />
          </Box>
        )}
      </Box>
    </Card>
  );
}
